import { apiUrl } from '@/lib/api';
import { getJson } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, CalendarDays } from 'lucide-react';
import { Select } from '@/components/primitives';
import { PlatformChip, RankedTitleCard, SegmentedControl } from '@/components/organisms';
import { greeting, prettyQuarter, shortPlatform } from '@/lib/format';
import styles from './HomeNext.module.css';

const TOP_N = 10;
/** Top 5 by subscribers — the rest live on the Platforms leaderboard. */
const PLATFORM_LIMIT = 5;
/** First name only from the stored full name ("Pranav Bhave" → "Pranav"). */
const getUserName = () => (localStorage.getItem('user_name') || '').split(' ')[0];

/**
 * Shows and movies are ranked separately in top_titles_quarterly, so an "all"
 * list interleaves two rankings. The toggle lets you look at one at a time.
 */
const CONTENT_TYPES = [
  { value: '', label: 'All' },
  { value: 'show', label: 'TV Shows' },
  { value: 'movie', label: 'Movies' },
];

interface PlatformEntry {
  platform: string;
  quarter: string;
  subscribers: number;
  subscriber_change: number;
}

interface TitleEntry {
  parrot_id: string;
  title: string;
  content_type: string;
  revenue_contribution_usd: number;
  revenue_share: number;
  release_year?: number;
  main_genre?: string;
}

/** '' means every region, summed. */
const ALL_REGIONS = '';
const REGIONS = [
  { value: ALL_REGIONS, label: 'All regions' },
  { value: 'UCAN', label: 'UCAN' },
  { value: 'EMEA', label: 'EMEA' },
  { value: 'APAC', label: 'APAC' },
  { value: 'LATAM', label: 'LATAM' },
];

/** Sentinel for "every quarter to date, cumulative". */
const ALL_TIME = 'all';



/**
 * Home 1.5 — greeting, a platform strip that carries one streaming metric,
 * and the Top 10s for whichever platform is selected.
 *
 * Deliberately three elements. Platform metrics beyond subscriber count live
 * on the Platforms leaderboard; duplicating them here competes with the cards.
 */
export function HomeNext() {
  const userName = getUserName();
  const [region, setRegion] = useState(ALL_REGIONS);
  const [quarter, setQuarter] = useState<string | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState('Netflix');
  const [contentType, setContentType] = useState('');

  const { data: filters } = useQuery({
    queryKey: ['platform-filters'],
    queryFn: () => getJson<{ quarters: string[] }>(apiUrl('/platforms/filters')),
    staleTime: 30 * 60 * 1000,
  });

  const isAllTime = quarter === ALL_TIME;

  const { data: platformData, isLoading: platformsLoading, isError: platformsError } = useQuery({
    queryKey: ['platform-leaderboard', region, quarter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (region) params.set('region', region);
      // Subscribers are a stock, not a flow — summing them across quarters is
      // meaningless, so "All time" shows the most recent quarter's standing.
      if (quarter && !isAllTime) params.set('quarter', quarter);
      return getJson<{ data: PlatformEntry[] }>(apiUrl(`/platforms/leaderboard?${params}`));
    },
    staleTime: 5 * 60 * 1000,
  });

  const platforms = useMemo(
    () => [...(platformData?.data ?? [])].sort((a, b) => b.subscribers - a.subscribers),
    [platformData]
  );

  /** The quarter the platform figures actually describe. */
  const latestQuarter = platformData?.data[0]?.quarter ?? null;
  const activeQuarter = isAllTime ? ALL_TIME : quarter ?? latestQuarter;

  const { data: titleData, isLoading: titlesLoading } = useQuery({
    queryKey: ['home-next-titles', selectedPlatform, region, activeQuarter, contentType],
    enabled: Boolean(activeQuarter),
    queryFn: () => {
      const params = new URLSearchParams({
        platform: selectedPlatform,
        timePeriod: activeQuarter as string,
        limit: String(TOP_N),
      });
      if (region) params.set('region', region);
      if (contentType) params.set('contentType', contentType);
      return getJson<{ data: TitleEntry[] }>(apiUrl(`/leaderboard?${params}`));
    },
    staleTime: 5 * 60 * 1000,
  });

  const titles = titleData?.data ?? [];

  const quarterOptions = [
    { value: ALL_TIME, label: 'All time' },
    ...(filters?.quarters ?? []).slice(0, 12).map((q) => ({
      value: q,
      label: prettyQuarter(q),
    })),
  ];

  /** Latest quarter from the filter list — the default when the user hasn't
      picked one. The platform rows report their own quarter in a different
      format ('2026 Q2' vs the filter's '2026-06-30'), so default off the
      options the dropdown actually offers or its value won't match one. */
  const latestFilterQuarter = filters?.quarters?.[0] ?? null;
  const selectedQuarter = quarter ?? latestFilterQuarter;

  const regionLabel = region || 'Worldwide';
  const periodLabel = isAllTime
    ? 'All time'
    : activeQuarter
      ? prettyQuarter(activeQuarter)
      : '—';

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.greeting}>
            {greeting()}{userName ? ` ${userName}` : ''}
          </h1>
          <p className={styles.welcome}>Welcome to Streaming Economics</p>
        </div>

        {/* Region and period scope both the platform strip and the Top 10s,
            so they sit above everything they affect. */}
        <div className={styles.scope}>
          <Select
            value={region}
            options={REGIONS}
            onChange={setRegion}
            icon={<Globe size={14} />}
            className={styles.scopeSelect}
          />
          <Select
            value={selectedQuarter ?? ALL_TIME}
            options={quarterOptions}
            onChange={setQuarter}
            icon={<CalendarDays size={14} />}
            className={styles.scopeSelect}
          />
        </div>
      </header>

      {platformsError && (
        <div className={styles.notice} role="alert">
          <strong>Couldn't load platforms.</strong> The API server may not be running.
        </div>
      )}

      {/* Platform strip — selector and streaming metric in one control */}
      <section className={styles.stripSection} aria-label="Platforms">
        {platformsLoading ? (
          <div className={styles.strip}>
            {Array.from({ length: PLATFORM_LIMIT }).map((_, i) => (
              <div key={i} className={styles.chipSkeleton} />
            ))}
          </div>
        ) : (
          <div className={styles.strip}>
            {platforms.slice(0, PLATFORM_LIMIT).map((p) => (
              <PlatformChip
                key={p.platform}
                platform={p.platform}
                subscribers={p.subscribers}
                subscriberChange={p.subscriber_change}
                selected={p.platform === selectedPlatform}
                onSelect={setSelectedPlatform}
              />
            ))}
            {platforms.length > PLATFORM_LIMIT && (
              <Link to="/platforms/leaderboard" className={styles.seeAll}>
                See all →
              </Link>
            )}
          </div>
        )}
      </section>

      {/* Top 10s for the selected platform */}
      <section className={styles.topSection} aria-label="Top 10s">
        <div className={styles.sectionHead}>
          <div>
            <h2 className={styles.sectionTitleRow}>
              <span className={styles.sectionTitle}>Top 10s</span>
              <span className={styles.sectionTitleDivider} aria-hidden="true">|</span>
              <span className={styles.sectionContext}>
                {shortPlatform(selectedPlatform)} · {regionLabel} · {periodLabel}
              </span>
              <span className={styles.sectionTitleDivider} aria-hidden="true">|</span>
              <Link
                to={`/titles/leaderboard?${new URLSearchParams({
                  platform: selectedPlatform,
                  ...(region ? { region } : {}),
                  ...(contentType ? { contentType } : {}),
                })}`}
                className={styles.headingLink}
              >
                Full leaderboard
              </Link>
            </h2>
          </div>

          <div className={styles.controls}>
            <SegmentedControl
              options={CONTENT_TYPES}
              value={contentType}
              onChange={setContentType}
            />
          </div>
        </div>

        {titlesLoading && (
          <div className={styles.grid}>
            {Array.from({ length: TOP_N }).map((_, i) => (
              <div key={i} className={styles.cardSkeleton} />
            ))}
          </div>
        )}

        {!titlesLoading && titles.length === 0 && (
          <div className={styles.empty}>
            <h3 className={styles.emptyTitle}>No titles for this combination</h3>
            <p className={styles.emptyBody}>
              {shortPlatform(selectedPlatform)} has no ranked titles for {regionLabel} ·{' '}
              {periodLabel}. Try another region or time period.
            </p>
          </div>
        )}

        {titles.length > 0 && (
          <div className={styles.grid}>
            {titles.map((t, i) => (
              <RankedTitleCard
                key={`${t.parrot_id}-${i}`}
                rank={i + 1}
                parrotId={t.parrot_id}
                title={t.title}
                releaseYear={t.release_year}
                genre={t.main_genre}
                contentType={t.content_type}
                revenueUsd={t.revenue_contribution_usd}
                revenueShare={t.revenue_share}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
